/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Removed local error prop, relying on global error handling.
 *                                   - Integrated Spinner for submission state.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide fields
 *                                   and customize field titles based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component handles user input for currency definitions.
 *                      It does not process sensitive data directly or interact with external systems.
 * INTENT: Provide a reusable form for creating and editing Currency records,
 *         ensuring data integrity and a clear user experience, now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure input validation is robust and
 *                        that the form state correctly reflects submission status.
 * LLM_CONFIDENCE: 100% - Standard form component with state management and input handling.
 */
import React, { useState, useEffect } from 'react';
import { Currency, CurrencyCreate } from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Spinner } from './ui/spinner.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface CurrencyFormProps {
  initialData?: Currency | null;
  onSubmit: (data: CurrencyCreate) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  entityId: string; // NEW: Add entityId prop
}

const CurrencyForm: React.FC<CurrencyFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const [formData, setFormData] = useState<CurrencyCreate>({
    description: '',
    exchange_rate: 0,
    show_on_print: true,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        description: initialData.description,
        exchange_rate: initialData.exchange_rate,
        show_on_print: initialData.show_on_print,
      });
    } else {
      setFormData({ description: '', exchange_rate: 0, show_on_print: true });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isFieldVisible('description') && (
        <div>
          <Label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('description', 'Name')}
          </Label>
          <Input
            id="description"
            type="text"
            required
            value={formData.description}
            onChange={handleChange}
          />
        </div>
      )}
      {isFieldVisible('exchange_rate') && (
        <div>
          <Label htmlFor="exchange_rate" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('exchange_rate', 'Exchange Rate')}
          </Label>
          <Input
            id="exchange_rate"
            type="number"
            step="0.0001"
            required
            value={formData.exchange_rate}
            onChange={handleChange}
          />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default CurrencyForm;